"use client";

import { useState, useRef, useEffect, useCallback, memo, useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import CdnImage from "@/components/CdnImage";
import GradientAvatar from "@/app/components/GradientAvatar";
import { TrendingUp, Search, Filter, RefreshCw, Wallet, Trophy, Target, Star, Users, Sparkles } from "lucide-react";
import { EyeIcon } from "@/app/components/icons/EyeIcon";
import { SwordsIcon } from "@/app/components/icons/SwordsIcon";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/hooks/useT";
import { createLogger } from "@/lib/logger";

const logger = createLogger("UsersGrid");
import { triggerConfetti } from "@/lib/confetti";
import { type PolymarketTrader } from "@/lib/polymarket";
import FrenFlowBadge from "@/app/components/FrenFlowBadge";
import TradingStyleBadge from "@/app/components/TradingStyleBadge";
import { formatCents } from "@/lib/format-cents";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { BsLightningCharge } from "react-icons/bs";

/**
 * A "placeholder" trader is a row inserted by the watchlist hydration
 * effect for an address whose profile hasn't been fetched yet. Detect
 * shape: username equals the truncated address ("0x17e0…fe5b") and no
 * frenflow identity attached. Both grid and list views render a
 * skeleton row for these instead of the broken-looking real card, so
 * the first visit to /traders?filter=watchlist doesn't flash anonymous
 * rows for 200-500ms while /api/traders/batch resolves.
 */
function isPlaceholderTrader(trader: PolymarketTrader): boolean {
  if (!trader?.address) return false;
  const truncated = `${trader.address.slice(0, 6)}...${trader.address.slice(-4)}`;
  return trader.username === truncated && !trader.frenFlowAvatar && !trader.isFrenFlowUser;
}

/** Shimmer card matching UserCard's footprint — used while batch loads. */
function UserCardSkeleton() {
  return (
    <div className="relative bg-gray-50/50 dark:bg-white/[0.02] rounded-[5px] overflow-hidden h-full flex flex-col animate-pulse">
      <div className="p-3 pb-2">
        <div className="flex items-start gap-2.5">
          <div className="w-11 h-11 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
          <div className="flex-1 min-w-0">
            <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-24 mb-1.5" />
            <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded w-16" />
          </div>
          <div className="w-8 h-8 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
        </div>
      </div>
      <div className="px-3 pb-2">
        <div className="p-2 bg-gray-100 dark:bg-white/[0.06] rounded-[5px]">
          <div className="h-3 bg-gray-200 dark:bg-[#0a0a0b] rounded w-8 mb-1" />
          <div className="h-5 bg-gray-200 dark:bg-[#0a0a0b] rounded w-20" />
        </div>
      </div>
      <div className="px-3 pb-3 mt-auto">
        <div className="grid grid-cols-3 gap-1.5">
          {[1, 2, 3].map((j) => (
            <div key={j} className="bg-gray-100 dark:bg-white/[0.06] rounded-[5px] p-2">
              <div className="h-2 bg-gray-200 dark:bg-[#0a0a0b] rounded w-8 mx-auto mb-1" />
              <div className="h-3 bg-gray-200 dark:bg-[#0a0a0b] rounded w-12 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Shimmer row for the list/table view. Width matches the visible
 *  columns at common breakpoints; details don't need to be exact — the
 *  row is replaced as soon as /api/traders/batch returns. */
function UserRowSkeleton({ colSpan }: { colSpan: number }) {
  return (
    <tr className="animate-pulse">
      <td className="px-2 py-2.5 sticky left-0 z-10 bg-white dark:bg-[#18181D]">
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 bg-gray-200 dark:bg-white/[0.06] rounded" />
          <div className="w-7 h-7 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
          <div className="flex-1 min-w-0">
            <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded w-20 mb-1" />
            <div className="h-2 bg-gray-200 dark:bg-white/[0.06] rounded w-12" />
          </div>
        </div>
      </td>
      <td colSpan={colSpan - 1} className="px-3 py-2.5">
        <div className="h-3 bg-gray-100 dark:bg-white/[0.04] rounded w-full max-w-[200px]" />
      </td>
    </tr>
  );
}

/** Tooltip that actually works — uses state, not CSS hover */
function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-help transition-colors" />
      {show && (
        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-[#18181D] border border-[#1A1A1A] rounded-[5px] text-[11px] text-gray-300 font-normal normal-case whitespace-normal w-52 text-left shadow-[0_4px_20px_rgba(0,0,0,0.5)] z-[999] pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

/** Table header with optional tooltip + sort indicator */
function TH({ children, sort, info, active, onSort, hide, align = "right", className = "" }: {
  children: React.ReactNode;
  sort?: string;
  info?: string;
  active?: string;
  onSort?: (s: string) => void;
  hide?: "md" | "lg" | "xl";
  align?: "left" | "center" | "right";
  className?: string;
}) {
  const isActive = sort && active === sort;
  const hideClass = hide === "md" ? "" : hide === "lg" ? "" : hide === "xl" ? "" : "";
  const alignClass = align === "left" ? "text-left" : align === "center" ? "text-center" : "text-right";

  const inner = (
    <span className={`inline-flex items-center gap-1 ${sort ? 'cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors' : ''} ${isActive ? 'text-gray-900 dark:text-white' : ''}`}
      onClick={sort ? () => onSort?.(sort) : undefined}
    >
      {children}
      {isActive && <ChevronDown className="w-3 h-3" />}
      {sort && !isActive && <ChevronDown className="w-3 h-3 opacity-0" />}
      {info && <InfoTooltip text={info} />}
    </span>
  );

  return (
    <th className={`px-2 sm:px-2.5 py-3 ${alignClass} text-xs font-sans font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap ${hideClass} ${className}`}>
      {align === "right" ? <div className="flex justify-end">{inner}</div> : inner}
    </th>
  );
}

// Utility functions (copied to avoid importing server-side db code)
function formatVolume(volume: number | undefined | null): string {
  const numVolume = Number(volume) || 0;
  if (numVolume >= 1000000) {
    return `$${(numVolume / 1000000).toFixed(1)}M`;
  } else if (numVolume >= 1000) {
    return `$${(numVolume / 1000).toFixed(1)}K`;
  }
  return `$${numVolume.toFixed(0)}`;
}

function formatCurrency(value: number | undefined | null): string {
  const numValue = Number(value) || 0;
  if (Math.abs(numValue) >= 1000000) {
    return `$${(numValue / 1000000).toFixed(2)}M`;
  } else if (Math.abs(numValue) >= 1000) {
    return `$${(numValue / 1000).toFixed(2)}K`;
  }
  return `$${numValue.toFixed(2)}`;
}

function getTraderAvatar(avatar: string | undefined | null): string | null {
  if (avatar && avatar.trim() !== "" && !avatar.includes("fallback-image.png")) {
    return avatar;
  }
  return null;
}

// Generate URL: always use Polymarket username/address (not FF username)
// /traders/ namespace = Polymarket identifiers to avoid collisions with FF usernames
function getTraderUrl(trader: PolymarketTrader, source = "polymarket"): string {
  if (source === "kalshi") {
    return `/traders/${trader.username || trader.address}?source=kalshi`;
  }
  if (source === "predict") {
    return `/traders/${trader.address}?source=predict`;
  }
  if (trader.username && !trader.username.startsWith("0x")) {
    return `/traders/@${trader.username}`;
  }
  return `/traders/${trader.address}`;
}

// Format display name: prefer FrenFlow username > PM username > truncated address
function formatDisplayName(trader: PolymarketTrader, source?: string): string {
  // Kalshi: just use username directly (no @ prefix)
  if (source === 'kalshi') {
    return trader.username || trader.address;
  }
  // Display names without the "@" prefix (visual only — getTraderUrl still
  // routes by @username). Prefer FrenFlow username.
  if (trader.isFrenFlowUser && trader.frenFlowUsername) {
    return trader.frenFlowUsername;
  }
  // Fallback to Polymarket username (skip hex addresses and timestamp-appended names)
  const isHexUsername = trader.username?.startsWith("0x");
  const isTimestampUsername = trader.username && /-\d{13,}$/.test(trader.username);
  if (trader.username && !isHexUsername && !isTimestampUsername) {
    return trader.username;
  }
  return `${trader.address.slice(0, 6)}...${trader.address.slice(-4)}`;
}

interface UsersGridProps {
  traders: PolymarketTrader[];
  watchlistAddresses: string[];
  onWatchlistChange: (addresses: string[]) => void;
  viewMode?: "grid" | "list";
  activeFilter?: string;
  onClearFilters?: () => void;
  compareTraders?: PolymarketTrader[];
  onAddToCompare?: (trader: PolymarketTrader) => void;
  sortBy?: string;
  onSort?: (sort: string) => void;
  source?: string;
  sparklines?: Record<string, number[]>;
  onCopy?: (trader: PolymarketTrader) => void;
  /** When true, render the X-handle row on each card linking to the
   *  trader's X profile. Driven by the founder/partner X-only toggle
   *  in UsersGridWrapper — only true when that toggle is on. */
  showXHandleRow?: boolean;
  /** Omit the trend chart/sparkline (list column + grid card). For Recommended
   *  this is now only transient — true until the curator emits copy-adjusted
   *  equity sparks; once present the chart shows. */
  hideChart?: boolean;
  /** Recommended (curator gems) tab. Uses the trimmed list column set
   *  (style·chart·roi·pnl·win·maxDD·copy) instead of the dense leaderboard
   *  table, since gems don't carry edge/big-win/profit-factor/avg-buy. */
  isRecommended?: boolean;
  /** Active timeframe label (e.g. "30D") prefixed onto card metric labels to
   *  mirror the Phemex copy-trading card. Omitted → no prefix. */
  timeframeLabel?: string;
  /** HIDE_PREDEXON + a specific 1d/7d/30d window: the free PM windowed source
   *  only carries PnL+Volume, so the list drops trades/bigwin/balance for these
   *  views. False (or all_time) keeps the full free column set. */
  pmWindowedView?: boolean;
}

export default function UsersGrid({ traders, watchlistAddresses, onWatchlistChange, viewMode = "grid", activeFilter, onClearFilters, compareTraders = [], onAddToCompare, sortBy, onSort, source = "polymarket", sparklines = {}, onCopy, showXHandleRow = false, hideChart = false, isRecommended = false, timeframeLabel, pmWindowedView = false }: UsersGridProps) {
  const t = useT();
  const router = useRouter();
  const { isLoading, isReady, isLoggedIn, currentUserId, currentWalletAddress } = useAuth();

  // ALL hooks MUST be before any early return (React rules of hooks)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => setIsScrolled(el.scrollLeft > 10);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [viewMode]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const toggleWatchlist = async (traderAddress: string, buttonElement?: HTMLButtonElement) => {
    if (!isLoggedIn) return;

    const isInWatchlist = watchlistAddresses.includes(traderAddress.toLowerCase());

    // Optimistic update
    if (isInWatchlist) {
      onWatchlistChange(watchlistAddresses.filter((addr) => addr !== traderAddress.toLowerCase()));
    } else {
      onWatchlistChange([...watchlistAddresses, traderAddress.toLowerCase()]);

      // Show confetti explosion at button position
      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        const x = (rect.left + rect.width / 2) / window.innerWidth;
        const y = (rect.top + rect.height / 2) / window.innerHeight;

        triggerConfetti({
          particleCount: 100,
          spread: 70,
          origin: { x, y },
          colors: ["#10B981", "#34D399", "#6EE7B7", "#A7F3D0", "#059669", "#047857"],
        });
      }
    }

    try {
      if (isInWatchlist) {
        // Favorites are per-source: scope the removal to the active platform.
        await fetch(`/api/watchlist/${traderAddress}?source=${source}`, {
          method: "DELETE",
        });
      } else {
        await fetch("/api/watchlist", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ address: traderAddress, source }),
        });
      }
    } catch (error) {
      logger.error("Error updating watchlist", { error: error instanceof Error ? error.message : String(error) });
      // Revert on error
      if (isInWatchlist) {
        onWatchlistChange([...watchlistAddresses, traderAddress.toLowerCase()]);
      } else {
        onWatchlistChange(watchlistAddresses.filter((addr) => addr !== traderAddress.toLowerCase()));
      }
    }
  };

  if (traders.length === 0) {
    // Contextual empty state based on active filter
    const emptyStates: Record<string, { icon: React.ReactNode; title: string; description: string; action?: string }> = {
      watchlist: {
        icon: <EyeIcon size={48} className="text-emerald-500" />,
        title: t("traders.empty.watchlist.title"),
        description: t("traders.empty.watchlist.desc"),
        action: t("traders.empty.watchlist.cta")
      },
      profitable: {
        icon: <TrendingUp className="w-12 h-12 text-emerald-400" />,
        title: t("traders.empty.profitable.title"),
        description: t("traders.empty.profitable.desc"),
      },
      whales: {
        icon: <span className="text-5xl">🐋</span>,
        title: t("traders.empty.whales.title"),
        description: t("traders.empty.whales.desc"),
      },
      "high-volume": {
        icon: <span className="text-5xl">📊</span>,
        title: t("traders.empty.highVolume.title"),
        description: t("traders.empty.highVolume.desc"),
      },
      default: {
        icon: <Search className="w-12 h-12 text-gray-400" />,
        title: t("traders.empty.default.title"),
        description: t("traders.empty.default.desc"),
      }
    };

    const state = emptyStates[activeFilter || ""] || emptyStates.default;

    return (
      <div className="flex flex-col items-center justify-center py-16 lg:py-24 px-4">
        <div className="mb-4 p-4 rounded-[5px] bg-gray-100 dark:bg-[#0a0a0b]">
          {state.icon}
        </div>
        <h3 className="text-lg lg:text-xl font-sans font-bold text-gray-900 dark:text-white mb-2 text-center">
          {state.title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 font-sans text-center max-w-md mb-6">
          {state.description}
        </p>
        {onClearFilters && activeFilter && activeFilter !== "all" && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 font-sans font-semibold text-sm rounded-[5px] transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t("traders.empty.clearFilters")}
          </button>
        )}
      </div>
    );
  }

  // Column definitions for Polymarket (reorderable per viewport)
  // 2026-06-15: dropped style/edge/pf/avgbuy (low-signal jargon already
  // removed from grid/carousel/profile) + bigwin (backend maps it from a
  // market's realized_pnl, which can be a LOSS → "-1.93M" on a column that
  // claims "biggest win"; also cherry-picked, low signal). HFT badge moved
  // inline next to the name. Chart sits 2nd as a visual anchor; the three
  // hard metrics (PnL · ROI · Win%) stay grouped, then Copy.
  // 2026-06-18: restored the dense analytics column set (style/edge/big win/
  // profit factor/avg buy) — the "cards minimal" pass (2026-06-15) had trimmed
  // the table to 5 columns, leaving huge dead gaps on wide screens. Win% kept
  // alongside the restored columns.
  type ColId = 'style' | 'chart' | 'pnl' | 'roi' | 'win' | 'edge' | 'bigwin' | 'pf' | 'avgbuy' | 'mdd' | 'copy' | 'volume' | 'trades' | 'balance';
  // While Predexon is down its columns (style/roi/win/edge/pf/avgbuy/chart) are
  // all "—". Swap to a set backed only by PM/DB data so the list has real values.
  const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";
  // chart (sparkline) STAYS — for Polymarket it's PM PnL history
  // (fetchPnLHistoryFromPolymarket), not Predexon → works during the outage.
  // Predexon Pro outage: the windowed leaderboard path (1d/7d/30d) only carries
  // volume + pnl — tradeCount/currentBalance/largestWin come back 0. But the
  // all-time (no-window) path is the local MV/DB, sourced from Polymarket's FREE
  // data-api, and has ALL of them for real. So under HIDE_PREDEXON the wrapper
  // drops the time pills and forces the all-time path, letting us show the FULL
  // free column set (see UsersGridWrapper: window param is skipped).
  const PM_ORDER: ColId[] = ['pnl', 'chart', 'volume', 'trades', 'bigwin', 'balance', 'copy'];
  // Windowed view (1d/7d/30d under HIDE_PREDEXON): the free PM pm-fallback only
  // carries PnL + Volume per window, so drop the lifetime-only columns
  // (trades/bigwin/balance) that would come back 0.
  const PM_WINDOWED_ORDER: ColId[] = ['pnl', 'chart', 'volume', 'copy'];
  // 2026-05-20: dropped the standalone 'watch' column; the favorite
  // toggle moves into the sticky-left "Trader" cell (★ inline before
  // the avatar) so the saved/unsaved state lives next to the trader's
  // identity rather than as a separate column at the right edge.
  const DESKTOP_ORDER: ColId[] = ['style', 'chart', 'roi', 'pnl', 'win', 'edge', 'bigwin', 'pf', 'avgbuy', 'copy'];
  // Mobile: Copy is sticky right (separate).
  const MOBILE_ORDER: ColId[]  = ['roi', 'pnl', 'win', 'chart', 'style', 'edge', 'pf', 'avgbuy', 'bigwin'];
  // Recommended (curator gems): only the fields mapGemToTrader populates + the
  // copy-adjusted equity sparkline + Max drawdown. Dropping edge/bigwin/pf/avgbuy
  // avoids dead "—"/"$0.00" columns (gems don't carry those).
  const RECOMMENDED_ORDER: ColId[] = ['style', 'chart', 'roi', 'pnl', 'win', 'mdd', 'copy'];
  const COL_WIDTHS_DESKTOP: Record<ColId, number> = { style: 95, chart: 80, pnl: 90, roi: 65, win: 60, edge: 65, bigwin: 85, pf: 50, avgbuy: 55, mdd: 80, copy: 85, volume: 90, trades: 75, balance: 90 };
  const COL_WIDTHS_MOBILE: Record<ColId, number> = { style: 95, chart: 80, pnl: 90, roi: 65, win: 60, edge: 65, bigwin: 85, pf: 60, avgbuy: 70, mdd: 80, copy: 85, volume: 90, trades: 75, balance: 90 };

  // List View - Styled like /markets page
  if (viewMode === "list") {
    // Recommended (curator gems) uses the trimmed column set (drops the
    // analytics columns gems don't populate, adds Max drawdown). The chart
    // column drops only while there's no spark yet (hideChart). Leaderboard/
    // Kalshi keep the full dense table (no mdd column there).
    const colOrder = source === 'kalshi'
      ? DESKTOP_ORDER
      : isRecommended
        ? RECOMMENDED_ORDER.filter(id => !(hideChart && id === 'chart'))
        : HIDE_PREDEXON
          ? (pmWindowedView ? PM_WINDOWED_ORDER : PM_ORDER).filter(id => !(hideChart && id === 'chart'))
          : (isMobile ? MOBILE_ORDER : DESKTOP_ORDER).filter(id => id !== 'mdd' && !(hideChart && id === 'chart'));

    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
        {/* Mobile: stacked 2-line rows (full @name + Copy on line 1, ROI ·
            PnL · trades on line 2) so names aren't truncated to 4 chars and
            there's no horizontal scroll. Desktop keeps the dense table below.
            2026-06-10. */}
        <div className="sm:hidden divide-y divide-gray-200 dark:divide-white/[0.04]">
          {traders.map((trader) => {
            if (isPlaceholderTrader(trader)) {
              return <div key={trader.address} className="h-[58px] animate-pulse bg-gray-50 dark:bg-white/[0.02]" />;
            }
            const mPnl = trader.totalPnL || 0;
            const mInWatchlist = watchlistAddresses.includes(trader.address.toLowerCase());
            const mAvatar = getTraderAvatar(trader.frenFlowAvatar || trader.avatar);
            return (
              <div
                key={trader.address}
                onClick={() => router.push(getTraderUrl(trader, source))}
                className="flex items-center gap-2.5 px-3 py-2.5 active:bg-gray-50 dark:active:bg-white/[0.04] cursor-pointer"
              >
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWatchlist(trader.address, e.currentTarget); }}
                  className={`flex-shrink-0 p-1 rounded-[5px] ${mInWatchlist ? "text-yellow-400" : "text-gray-400"}`}
                  aria-label={mInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
                >
                  <Star size={15} className={mInWatchlist ? "fill-current" : ""} />
                </button>
                <div className="flex-shrink-0 w-9 h-9 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0a0a0b]">
                  {mAvatar ? (
                    <CdnImage src={mAvatar} alt={trader.username || "Trader"} width={56} height={56} className="w-full h-full object-cover scale-[1.2]" />
                  ) : (
                    <GradientAvatar seed={trader.address || "default"} size={36} className="w-full h-full !rounded-none" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="font-sans font-bold text-gray-900 dark:text-white text-[13px] truncate" title={formatDisplayName(trader, source)}>
                      {formatDisplayName(trader, source)}
                    </span>
                    {showXHandleRow && trader.xUsername && (
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-black text-white">
                        <svg className="w-2 h-2" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] mt-0.5 tabular-nums whitespace-nowrap">
                    {trader.roi != null && (
                      <>
                        <span className={trader.roi >= 0 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}>
                          {trader.roi >= 0 ? '+' : ''}{(trader.roi * 100).toFixed(1)}%
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                      </>
                    )}
                    <span className={mPnl >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                      {mPnl !== 0 ? `${mPnl >= 0 ? '+' : ''}${formatCurrency(mPnl)}` : '—'}
                    </span>
                    {(source === 'kalshi' || trader.tradeCount) && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {source === 'kalshi'
                            ? `#${trader.rank || '—'}`
                            : `${new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(trader.tradeCount || 0)} ${t("traders.card.tradesSuffix")}`}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {source !== 'kalshi' && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCopy?.(trader); }}
                    className="flex-shrink-0 w-9 h-9 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center active:scale-95 transition-colors hover:bg-gray-800 dark:hover:bg-gray-100"
                    aria-label={t("traders.col.copy")}
                  >
                    <BsLightningCharge className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div ref={scrollContainerRef} className="hidden sm:block overflow-x-auto scrollbar-thin scrollbar-thumb-gray-700">
          {/* The 1280 min-width fits the dense ~10-col Predexon table. With the
              slimmer PM column set during the outage, drop the forced min so the
              table fills the container (table-fixed stretches the cols) instead
              of leaving dead space / a scroll. */}
          <div className="w-full" style={{minWidth: isMobile ? 850 : (HIDE_PREDEXON ? undefined : 1280)}}>
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{width: HIDE_PREDEXON ? '15%' : (isMobile ? 120 : 140)}} />
              {source === 'kalshi' ? (
                <><col style={{width: 90}} /><col style={{width: 90}} /><col style={{width: 85}} /></>
              ) : (
                <>
                  {/* Percentage widths that sum to 100% (15% trader + 85%/N) so
                      table-fixed distributes the full container width instead of
                      packing content-sized cols left with dead space right. */}
                  {colOrder.map(id => <col key={id} style={HIDE_PREDEXON ? {width: `${85 / colOrder.length}%`} : {width: (isMobile ? COL_WIDTHS_MOBILE : COL_WIDTHS_DESKTOP)[id]}} />)}
                  {isMobile && <col style={{width: 40}} />}
                </>
              )}
            </colgroup>
            <thead className="border-b border-gray-200 dark:border-[#1A1A1A] sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-[#0a0a0b]">
                <th className={`px-2.5 py-3 text-left text-xs font-sans font-medium text-gray-500 dark:text-gray-400 sticky left-0 z-20 bg-gray-50 dark:bg-[#0a0a0b] transition-shadow duration-200 ${isScrolled ? 'shadow-[3px_0_6px_-2px_rgba(0,0,0,0.2)]' : ''}`}>
                  {/* Indent so "TRADER" sits above the avatar column, not
                      above the star button. Pad = star button width (22px:
                      14px icon + 4px padding × 2) + flex gap (8px) = 30px. */}
                  <button onClick={() => onSort?.("rank")} className={`flex items-center gap-1 pl-[30px] hover:text-gray-900 dark:hover:text-white transition-colors ${sortBy === "rank" ? "text-gray-900 dark:text-white" : ""}`}>
                    {t("traders.col.trader")} {sortBy === "rank" && <span className="text-[8px]">▼</span>}
                  </button>
                </th>
                {source === 'kalshi' ? (
                  <>
                    <th className="px-3 py-3 text-right text-xs font-sans font-medium text-gray-500 dark:text-gray-400">
                      <button onClick={() => onSort?.("pnl")} className={`ml-auto flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors ${sortBy === "pnl" ? "text-gray-900 dark:text-white" : ""}`}>
                        {t("traders.col.profit")} {sortBy === "pnl" && <span className="text-[8px]">▼</span>}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-sans font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      <button onClick={() => onSort?.("volume")} className={`ml-auto flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors ${sortBy === "volume" ? "text-gray-900 dark:text-white" : ""}`}>
                        {t("traders.col.volume")} {sortBy === "volume" && <span className="text-[8px]">▼</span>}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-sans font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      <span>{t("traders.col.followers")}</span>
                    </th>
                  </>
                ) : (
                  <>
                    {colOrder.map(id => {
                      const headers: Record<ColId, React.ReactNode> = {
                        style: <TH key={id} align="center">{t("traders.col.style")}</TH>,
                        pnl: <TH key={id} sort="pnl" info={t("traders.tip.pnl")} active={sortBy} onSort={onSort}>{t("traders.col.pnl")}</TH>,
                        roi: <TH key={id} info={t("traders.tip.roi")} align="right">{t("traders.col.roi")}</TH>,
                        win: <TH key={id} info={t("traders.tip.win")} align="right">{t("traders.col.win")}</TH>,
                        edge: <TH key={id} info={t("traders.tip.edge")} align="right">{t("traders.col.edge")}</TH>,
                        bigwin: <TH key={id} sort="bigwin" info="Largest single winning position" active={sortBy} onSort={onSort}>{source === 'predict' ? t("traders.col.volume") : t("traders.col.bigWin")}</TH>,
                        pf: <TH key={id} info={t("traders.tip.pf")} align="right">{t("traders.col.pf")}</TH>,
                        avgbuy: <TH key={id} info={t("traders.tip.avgBuy")} align="right">{t("traders.col.avgBuy")}</TH>,
                        mdd: <TH key={id} align="right">{t("traders.col.maxDD")}</TH>,
                        volume: <TH key={id} sort="volume" active={sortBy} onSort={onSort} align="right">{t("traders.col.volume")}</TH>,
                        trades: <TH key={id} align="right">{t("traders.card.trades")}</TH>,
                        balance: <TH key={id} sort="balance" active={sortBy} onSort={onSort} align="right">{t("traders.col.balance")}</TH>,
                        chart: <TH key={id} align="center">{t("traders.col.chart")}</TH>,
                        copy: <TH key={id} align="center">{t("traders.col.copy")}</TH>,
                      };
                      return headers[id];
                    })}
                    {/* Copy header — sticky right on mobile */}
                    {isMobile && (
                      <th className="px-1 py-3 text-center text-[10px] font-sans font-medium text-gray-500 dark:text-gray-400 sticky right-0 z-20 bg-gray-50 dark:bg-[#0a0a0b]" />
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/[0.04]">
              {traders.map((trader) => {
                // Placeholder row from watchlist hydration — render a
                // skeleton instead of the broken "0x17e0…fe5b · 0 trades"
                // visual until /api/traders/batch upgrades it.
                if (isPlaceholderTrader(trader)) {
                  // colSpan covers the restored polymarket column set (trader +
                  // style/chart/roi/pnl/win/edge/bigwin/pf/avgbuy/copy = 11). The
                  // skeleton stretches the second cell across the remainder so the
                  // row layout doesn't jitter when real data arrives.
                  return <UserRowSkeleton key={trader.address} colSpan={11} />;
                }
                const pnl = trader.totalPnL || 0;
                const isProfitable = pnl >= 0;
                const isInWatchlist = watchlistAddresses.includes(trader.address.toLowerCase());
                // Win% — API value if present, else derived from wins/(wins+losses).
                // Same recipe as the grid card hero subline (single source of truth).
                const rowTradeOutcomes = (trader.wins || 0) + (trader.losses || 0);
                const rowWinRate = trader.winRate > 0
                  ? Math.round(trader.winRate)
                  : rowTradeOutcomes > 0
                    ? Math.round(((trader.wins || 0) / rowTradeOutcomes) * 100)
                    : null;

                return (
                  <tr
                    key={trader.address}
                    className="hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors duration-100 cursor-pointer group"
                    onClick={() => router.push(getTraderUrl(trader, source))}
                    onMouseEnter={() => router.prefetch(getTraderUrl(trader, source))}
                  >
                    {/* Trader — sticky left, always shows favorite ★ + avatar + name */}
                    <td className={`px-2 py-2.5 sticky left-0 z-10 bg-white dark:bg-[#18181D] transition-shadow duration-200 ${isScrolled ? 'shadow-[3px_0_6px_-2px_rgba(0,0,0,0.2)]' : ''} group-hover:bg-gray-50 dark:group-hover:bg-[#1f1f24]`}>
                      <div className="flex items-center gap-2 overflow-hidden">
                        {/* Favorite toggle — lives next to the trader's
                            identity (avatar + name) so the saved state
                            reads as "this user is in my list" rather
                            than as a separate utility column. The eye
                            icon + Watch column we used to have was
                            confusing (eye = view, not save). */}
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWatchlist(trader.address, e.currentTarget); }}
                          className={`flex-shrink-0 p-1 rounded-[5px] transition-colors ${isInWatchlist ? "text-yellow-400 hover:bg-yellow-400/10" : "text-gray-400 hover:text-yellow-400 hover:bg-white/[0.06]"}`}
                          title={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
                          aria-label={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
                        >
                          <Star size={14} className={isInWatchlist ? "fill-current" : ""} />
                        </button>
                        <div className="flex-shrink-0 w-7 h-7 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0a0a0b]">
                          {getTraderAvatar(trader.frenFlowAvatar || trader.avatar) ? (
                            <CdnImage
                              src={getTraderAvatar(trader.frenFlowAvatar || trader.avatar)!}
                              alt={trader.username || "Trader"}
                              width={56}
                              height={56}
                              // scale-[1.2] crops the transparent margin
                              // most Polymarket avatars carry around the
                              // subject, so the visible portrait matches
                              // the size of GradientAvatar cells.
                              className="w-full h-full object-cover scale-[1.2]"
                            />
                          ) : (
                            <GradientAvatar seed={trader.address || "default"} size={28} className="w-full h-full !rounded-none" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="font-sans font-bold text-white text-[12px] truncate group-hover:text-gray-300 transition-colors duration-100" title={formatDisplayName(trader, source)}>
                              {formatDisplayName(trader, source)}
                            </span>
                            {/* HFT badge lives in the Style column (restored
                                2026-06-18), so it's no longer rendered inline. */}
                            {/* X handle — small logo next to the name (replaces
                                the old dedicated column that shifted the table
                                width when the founder X-only filter toggled). */}
                            {showXHandleRow && trader.xUsername && (
                              <a
                                href={`https://x.com/${trader.xUsername}`}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-black text-white hover:opacity-80 transition-opacity"
                                title={t("traders.tip.xOnHandle", { handle: trader.xUsername })}
                                aria-label={t("traders.tip.xOnHandle", { handle: trader.xUsername })}
                              >
                                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                              </a>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-500 font-mono truncate">
                            {source === 'kalshi'
                              ? `#${trader.rank || '—'}`
                              : trader.tradeCount
                                ? `${trader.tradeCount.toLocaleString()} ${t("traders.card.tradesSuffix")}`
                                : ''}
                          </div>
                        </div>
                      </div>
                    </td>

                    {source === 'kalshi' ? (
                      <>
                        {/* Profit */}
                        <td className="px-2 sm:px-3 py-3 text-right tabular-nums">
                          {pnl !== 0 ? (
                            <span className={`text-xs sm:text-sm font-sans font-semibold whitespace-nowrap ${isProfitable ? 'text-emerald-500' : 'text-red-500'}`}>
                              {isProfitable ? '+' : ''}{formatCurrency(pnl)}
                            </span>
                          ) : (
                            <span className="text-xs sm:text-sm font-sans text-gray-400">—</span>
                          )}
                        </td>
                        {/* Volume */}
                        <td className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                          <span className={`text-sm font-sans ${trader.totalVolume ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                            {trader.totalVolume ? formatVolume(trader.totalVolume) : '—'}
                          </span>
                        </td>
                        {/* Followers */}
                        <td className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                          <span className="text-sm font-sans font-medium text-gray-900 dark:text-white">
                            {trader.followers ? trader.followers.toLocaleString() : '—'}
                          </span>
                        </td>
                      </>
                    ) : (
                      colOrder.map(id => {
                        const cells: Record<ColId, React.ReactNode> = {
                          style: (
                            <td key={id} className="px-2 py-3 text-center">
                              {/* HFT badge overrides primaryStyle — activity > 200
                                  trades/h is a stronger copy-trade signal than the
                                  general style; followers need to know before the
                                  rate-limit wall. */}
                              {trader.isHft ? (
                                <span
                                  className="inline-block px-2 py-0.5 rounded-[5px] text-[10px] font-bold whitespace-nowrap bg-red-500/15 text-red-500 border border-red-500/30"
                                  title={trader.tradesPerHour
                                    ? t("traders.hftBadgeTooltipWithRate", { rate: trader.tradesPerHour })
                                    : t("traders.hftBadgeTooltip")}
                                >
                                  🚨 {t("traders.hftBadge")}
                                </span>
                              ) : trader.primaryStyle ? (
                                <TradingStyleBadge style={trader.primaryStyle} size="sm" />
                              ) : <span className="text-[10px] text-gray-600">—</span>}
                            </td>
                          ),
                          pnl: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              <span className={`text-xs font-bold ${isProfitable ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}>
                                {isProfitable ? '+' : ''}{formatCurrency(pnl)}
                              </span>
                            </td>
                          ),
                          edge: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {trader.entryEdge != null ? (
                                <span className={`text-xs font-semibold ${trader.entryEdge <= 0 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}>
                                  {trader.entryEdge <= 0 ? '' : '+'}{(trader.entryEdge * 100).toFixed(1)}%
                                </span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          bigwin: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              <span className="text-xs font-semibold text-[#00FF9D]">
                                {source === 'predict' ? formatVolume(trader.totalVolume) : formatCurrency(trader.largestWin)}
                              </span>
                            </td>
                          ),
                          pf: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {trader.profitFactor != null ? (
                                <span className={`text-xs font-semibold ${trader.profitFactor >= 1 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}>
                                  {trader.profitFactor.toFixed(2)}
                                </span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          avgbuy: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {trader.avgBuyPrice != null ? (
                                <span className="text-xs text-gray-400">{formatCents(trader.avgBuyPrice, 2)}</span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          roi: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {trader.roi != null ? (
                                <span className={`text-xs font-semibold ${trader.roi >= 0 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}>
                                  {trader.roi >= 0 ? '+' : ''}{(trader.roi * 100).toFixed(1)}%
                                </span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          mdd: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {trader.maxDrawdownPct != null ? (
                                <span className="text-xs font-semibold text-amber-500">−{Math.round(trader.maxDrawdownPct)}%</span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          volume: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              <span className="text-xs font-semibold text-gray-900 dark:text-white">{trader.totalVolume ? formatVolume(trader.totalVolume) : '—'}</span>
                            </td>
                          ),
                          trades: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {trader.tradeCount ? (
                                <span className="text-xs text-gray-900 dark:text-white">{trader.tradeCount.toLocaleString()}</span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          balance: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {trader.currentBalance ? (
                                <span className="text-xs text-gray-900 dark:text-white">{formatCurrency(trader.currentBalance)}</span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          win: (
                            <td key={id} className="px-2.5 py-3 text-right whitespace-nowrap tabular-nums">
                              {rowWinRate != null ? (
                                <span className="text-xs font-semibold text-gray-900 dark:text-white">{rowWinRate}%</span>
                              ) : <span className="text-xs text-gray-600">—</span>}
                            </td>
                          ),
                          chart: (
                            <td key={id} className="px-2 py-3">
                              {(() => {
                                const spData = sparklines[trader.address] || sparklines[trader.address.toLowerCase()];
                                if (!spData || spData.length < 2) return <span className="text-[10px] text-gray-600">—</span>;
                                return <MiniSparkline data={spData} color={isProfitable ? '#00FF9D' : '#FF4444'} id={`list-${trader.address}`} />;
                              })()}
                            </td>
                          ),
                          copy: (
                            <td key={id} className="px-3 py-3 text-center">
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCopy?.(trader); }}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[11px] font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors active:scale-95 whitespace-nowrap"
                                title={t("traders.tip.copyTable")}
                              >
                                <BsLightningCharge className="w-3.5 h-3.5 flex-shrink-0" />
                                {t("traders.col.copy")}
                              </button>
                            </td>
                          ),
                        };
                        return cells[id];
                      })
                    )}
                    {/* Copy — sticky right on mobile */}
                    {isMobile && source !== 'kalshi' && (
                      <td className="px-1 py-2.5 text-center sticky right-0 z-10 bg-white dark:bg-[#18181D] group-hover:bg-gray-50 dark:group-hover:bg-[#1f1f24]">
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCopy?.(trader); }}
                          className="w-8 h-8 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center mx-auto active:scale-95 hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                          title={t("traders.tip.copyMobile")}
                          aria-label={t("traders.col.copy")}
                        >
                          <BsLightningCharge className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    );
  }

  // Grid View (default) - Same design on all devices
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
      {traders.map((trader) =>
        isPlaceholderTrader(trader) ? (
          <UserCardSkeleton key={trader.address} />
        ) : (
          <UserCard
            key={trader.address}
            trader={trader}
            isInWatchlist={watchlistAddresses.includes(trader.address.toLowerCase())}
            onToggleWatchlist={toggleWatchlist}
            isLoggedIn={!!isLoggedIn}
            isInCompare={compareTraders.some((t) => t.address === trader.address)}
            onAddToCompare={onAddToCompare}
            canAddToCompare={compareTraders.length < 2}
            source={source}
            sortBy={sortBy}
            sparklineData={sparklines[trader.address] || sparklines[trader.address.toLowerCase()]}
            onCopy={onCopy}
            showXHandleRow={showXHandleRow}
            hideChart={hideChart}
            timeframeLabel={timeframeLabel}
          />
        ),
      )}
    </div>
  );
}

interface UserCardProps {
  trader: PolymarketTrader;
  isInWatchlist: boolean;
  onToggleWatchlist: (address: string, buttonElement?: HTMLButtonElement) => void;
  isLoggedIn: boolean;
  isInCompare?: boolean;
  onAddToCompare?: (trader: PolymarketTrader) => void;
  canAddToCompare?: boolean;
  source?: string;
  sortBy?: string;
  sparklineData?: number[];
  hideChart?: boolean;
  /** Active timeframe label (e.g. "30D") prefixed onto metric labels. */
  timeframeLabel?: string;
  /** Opens the copytrading modal for this trader. Same callback as the
   *  list view's per-row Copy button (Pika 2026-05-22 — grid view was
   *  missing the primary CTA). */
  onCopy?: (trader: PolymarketTrader) => void;
  /** Render the X-handle row when the trader has an X linked AND the
   *  parent says so (founder X-only filter on). */
  showXHandleRow?: boolean;
}

// Mini sparkline component for PnL trend - Responsive Area Chart
function MiniSparkline({ data, color, id }: { data: number[]; color: string; id: string }) {
  const height = 28;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padding = 2; // Small padding to prevent clipping

  // Build line points (for the stroke)
  const linePoints = data.map((value, i) => {
    const x = (i / (data.length - 1)) * 100; // percentage-based
    const y = padding + (height - 2 * padding) - ((value - min) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(' ');

  // Build area path (closed shape for gradient fill)
  const areaPath = data.map((value, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = padding + (height - 2 * padding) - ((value - min) / range) * (height - 2 * padding);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ') + ` L 100 ${height} L 0 ${height} Z`;

  // Unique gradient ID based on trader
  const gradientId = `sparkGrad-${id}`;

  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className="w-full h-7 overflow-visible"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {/* Area fill with gradient */}
      <path
        d={areaPath}
        fill={`url(#${gradientId})`}
      />
      {/* Line stroke on top */}
      <polyline
        points={linePoints}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}


const UserCard = memo(function UserCard({ trader, isInWatchlist, onToggleWatchlist, isLoggedIn, isInCompare, onAddToCompare, canAddToCompare, source = "polymarket", sparklineData, onCopy, showXHandleRow = false, hideChart = false }: UserCardProps) {
  const t = useT();
  const traderUrl = getTraderUrl(trader, source);
  const pnl = trader.totalPnL || 0;
  const isProfitable = pnl >= 0;

  // Calculate win rate from API value or wins/(wins+losses)
  const totalTrades = (trader.wins || 0) + (trader.losses || 0);
  const winRate = trader.winRate > 0
    ? Math.round(trader.winRate)
    : totalTrades > 0
      ? Math.round(((trader.wins || 0) / totalTrades) * 100)
      : 0;

  // Use real sparkline data from API, fallback to seed-based placeholder
  const chartData = useMemo(() => {
    if (sparklineData && sparklineData.length >= 2) return sparklineData;
    // Fallback: seed-based placeholder until real data loads
    const seed = trader.address.charCodeAt(0) + trader.address.charCodeAt(2);
    return Array.from({ length: 12 }, (_, i) => {
      const base = 50;
      const trend = isProfitable ? i * 3 : -i * 2;
      const noise = ((seed + i * 7) % 15) - 7.5;
      return base + trend + noise;
    });
  }, [sparklineData, trader.address, isProfitable]);

  const handleWatchlistClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleWatchlist(trader.address, e.currentTarget);
  };

  const handleCompareClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isInCompare && canAddToCompare && onAddToCompare) {
      onAddToCompare(trader);
    }
  };

  return (
    <Link href={traderUrl} prefetch={false} className="group relative block h-full">
      {/* 2026-05-22 (Pika): h-full lets the grid stretch each card to
          the tallest card in the same row, keeping the bottom CTA
          aligned across the row without a manual min-height that
          would create empty space on cards with less content.
          flex-col keeps the layout consistent; cards are uniform height now
          that the bio is gone, so the stats block ends each card evenly. */}
      {/* Style B (Minimal/Linear) — centered column, applied 2026-06-15.
          Compare + favorite sit absolute top-right so the centered hero stays
          clean; Copy is the full-width outline CTA at the bottom. */}
      <div className="relative h-full flex flex-col gap-3 rounded-[10px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#18181D] p-4 transition-colors hover:border-gray-300 dark:hover:border-white/20">
        <div className="absolute top-2 right-2 z-10 flex items-center gap-0.5">
          {source !== "kalshi" && onAddToCompare && (
            <button
              onClick={handleCompareClick}
              disabled={isInCompare || !canAddToCompare}
              className={`p-1.5 rounded-[5px] transition-colors ${
                isInCompare
                  ? "bg-emerald-500/10 text-emerald-500"
                  : canAddToCompare
                    ? "text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-700 dark:hover:text-gray-300"
                    : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
              }`}
              title={isInCompare ? t("traders.tip.compareInList") : canAddToCompare ? t("traders.tip.compareAdd") : t("traders.tip.compareLimit")}
              aria-label={isInCompare ? t("traders.tip.compareInList") : canAddToCompare ? t("traders.tip.compareAdd") : t("traders.tip.compareLimit")}
            >
              <SwordsIcon size={16} />
            </button>
          )}
          {source !== "kalshi" && isLoggedIn && (
            <button
              onClick={handleWatchlistClick}
              className={`p-1.5 rounded-[5px] transition-colors ${
                isInWatchlist
                  ? "text-yellow-400 hover:bg-yellow-400/10"
                  : "text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-yellow-400"
              }`}
              aria-label={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
              title={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
            >
              <Star size={16} className={isInWatchlist ? "fill-current" : ""} />
            </button>
          )}
        </div>

        {/* Header — avatar + name + copiers (left-aligned, Bitget-style). The
            compare/favorite cluster sits absolute top-right (above), so the row
            reserves space on the right (pr-12). */}
        <div className="flex items-start gap-2.5 pr-12">
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-[5px] overflow-hidden">
              {getTraderAvatar(trader.frenFlowAvatar || trader.avatar) ? (
                <CdnImage
                  src={getTraderAvatar(trader.frenFlowAvatar || trader.avatar)!}
                  alt={trader.username || "Trader"}
                  width={96}
                  height={96}
                  className="w-full h-full object-cover scale-[1.2]"
                />
              ) : (
                <GradientAvatar seed={trader.address || "default"} size={40} className="w-full h-full !rounded-none" />
              )}
            </div>
            {showXHandleRow && trader.xUsername && (
              // role=link span (NOT <a>): the card itself is an <a> (Next Link);
              // an <a> inside an <a> is invalid HTML → hydration error.
              <span
                role="link"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(`https://x.com/${trader.xUsername}`, "_blank", "noopener,noreferrer");
                }}
                className="absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white text-black border-2 border-white dark:border-[#18181D] hover:opacity-80 transition-opacity cursor-pointer"
                title={t("traders.tip.xOnHandle", { handle: trader.xUsername })}
                aria-label={t("traders.tip.xOnHandle", { handle: trader.xUsername })}
              >
                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <span className="font-satoshi font-semibold text-[14px] text-gray-900 dark:text-white truncate group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                {formatDisplayName(trader, source)}
              </span>
              {trader.isFrenFlowUser && <FrenFlowBadge />}
              {trader.isHft && (
                <span
                  className="px-1.5 py-0.5 rounded text-[8px] font-bold leading-none whitespace-nowrap bg-red-500/15 text-red-500 border border-red-500/30 flex-shrink-0"
                  title={trader.tradesPerHour ? t("traders.hftBadgeTooltipWithRate", { rate: trader.tradesPerHour }) : t("traders.hftBadgeTooltip")}
                >
                  🚨 {t("traders.hftBadge")}
                </span>
              )}
            </div>
            {(trader.copierCount ?? 0) > 0 ? (
              <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                <Users className="w-3 h-3 flex-shrink-0" />
                <span className="tabular-nums">{trader.copierCount!.toLocaleString()}</span>
              </div>
            ) : (
              // 0 copiers → invitation instead of a bare "0" (which reads as a
              // negative signal). Pioneer FOMO; chosen over hiding the row.
              <div className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                <Sparkles className="w-3 h-3 flex-shrink-0" />
                <span>{t("traders.card.beFirst")}</span>
              </div>
            )}
          </div>
        </div>

        {/* ROI hero + sparkline (left-aligned). Hero = ROI when available,
            else PnL (Kalshi / roi-null). Sparkline sits to the right; hidden in
            Recommended (hideChart: not copy-adjusted, ignores timeframe pills). */}
        {(() => {
          const heroIsRoi = source !== "kalshi" && trader.roi != null && !(trader.roi === 0 && Math.abs(pnl) > 1);
          const roiVal = trader.roi ?? 0;
          const heroPositive = heroIsRoi ? roiVal >= 0 : isProfitable;
          const heroValue = heroIsRoi
            ? `${roiVal >= 0 ? '+' : ''}${(roiVal * 100).toFixed(2)}%`
            : `${isProfitable ? '+' : ''}${formatCurrency(pnl)}`;
          return (
            <div className="flex items-end justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                  {heroIsRoi ? t("traders.card.roi") : t("traders.card.pnl")}
                </div>
                <div className={`text-[26px] leading-none font-sans font-black tracking-tight ${heroPositive ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}>
                  {heroValue}
                </div>
                <div className="text-[11px] mt-1.5 whitespace-nowrap">
                  {heroIsRoi ? (
                    <>
                      <span className="text-gray-500 dark:text-gray-400">{t("traders.card.pnl")}: </span>
                      <span className={isProfitable ? 'text-emerald-500' : 'text-red-500'}>
                        {isProfitable ? '+' : ''}{formatCurrency(pnl)}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">
                      {/* Hide the "0 trades" subline when we have no trade count
                          (e.g. Predexon-fed field empty during an outage) — show
                          the win rate if we have it, otherwise nothing. */}
                      {trader.tradeCount
                        ? <>{totalTrades > 0 ? `${winRate}% · ` : ''}{trader.tradeCount.toLocaleString()} {t("traders.card.tradesSuffix")}</>
                        : totalTrades > 0
                          ? `${winRate}% ${t("traders.card.winRate")}`
                          : null}
                    </span>
                  )}
                </div>
              </div>
              {!hideChart && (
                <div className="w-[92px] h-11 flex-shrink-0">
                  <MiniSparkline data={chartData} color={heroPositive ? '#10B981' : '#EF4444'} id={trader.address} />
                </div>
              )}
            </div>
          );
        })()}

        {/* Stats block (label left · value right) — Phemex-style rows using
            metrics we reliably populate: Volume, Trades, Win Rate. The
            reference's "AUM" / "Copiers' profit" aren't metrics we compute
            per-leader, so we don't fabricate them. No timeframe prefix on the
            labels — the active window is already shown by the global selector
            and the ROI/PnL hero, so repeating "30D" per row is redundant. */}
        {/* Stats rows — each renders ONLY when its metric is present, so we
            never show a bare "—". Field availability differs by tab:
            Recommended (gems) has winRate + trades + (post-deploy) maxDrawdownPct
            but NO volume; Leaderboard has volume + trades + winRate but no MDD.
            No timeframe prefix: the active window is shown by the selector. */}
        {(() => {
          const tradesVal = trader.tradeCount || totalTrades || 0;
          const rows: React.ReactNode[] = [];
          // Max drawdown (Recommended, risk signal) — amber.
          if (trader.maxDrawdownPct != null) {
            rows.push(
              <div key="mdd" className="flex items-center justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-400">{t("traders.card.maxDrawdown")}</span>
                <span className="tabular-nums font-medium text-amber-500">−{Math.round(trader.maxDrawdownPct)}%</span>
              </div>
            );
          }
          // Volume (Leaderboard) — gems have none, so skip rather than show "—".
          if (trader.totalVolume) {
            rows.push(
              <div key="vol" className="flex items-center justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-400">{t("traders.card.volume")}</span>
                <span className="tabular-nums font-medium text-gray-900 dark:text-white">{formatVolume(trader.totalVolume)}</span>
              </div>
            );
          }
          if (tradesVal > 0) {
            rows.push(
              <div key="trades" className="flex items-center justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-400">{t("traders.card.trades")}</span>
                <span className="tabular-nums font-medium text-gray-900 dark:text-white">{tradesVal.toLocaleString()}</span>
              </div>
            );
          }
          // Win rate: guard on the value itself (gems carry winRate WITHOUT
          // wins/losses, so the old totalTrades>0 guard hid a present value).
          if (winRate > 0) {
            rows.push(
              <div key="win" className="flex items-center justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-400">{t("traders.card.winRate")}</span>
                <span className="tabular-nums font-medium text-gray-900 dark:text-white">{winRate}%</span>
              </div>
            );
          }
          if (rows.length === 0) return null;
          return <div className="border-t border-gray-200 dark:border-white/[0.06] pt-2.5 space-y-1.5">{rows}</div>;
        })()}

        {/* Copy Now — full-width, pinned to bottom (mt-auto) so CTAs align
            across the row. Subtle neutral button (reference style), de-limed. */}
        {onCopy && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCopy(trader); }}
            className="mt-auto w-full flex items-center justify-center gap-2 py-2.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.06] text-gray-900 dark:text-white font-satoshi font-semibold text-sm hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-colors"
          >
            <BsLightningCharge className="w-3.5 h-3.5 flex-shrink-0" />
            {t("traders.card.copyNow")}
          </button>
        )}

        {/* Footer removed 2026-06-10: compare moved to the header action
            cluster, copy-address became a small inline icon next to the
            address. The card now ends on the stats block. */}

      </div>
    </Link>
  );
});

